import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Baby, TrendingUp, Calendar, Clock, Home } from "lucide-react";
import RevealText from "../components/RevealText";
import { BRAND } from "../lib/brand";

const NEONATE_INCLUDES = [
  "Cura quotidiana del neonato",
  "Gestione del sonno e dei risvegli",
  "Supporto all'alimentazione",
  "Igiene e cambio",
  "Osservazione dello sviluppo",
  "Costruzione delle routine",
  "Supporto alla genitorialità",
  "Restituzione educativa ai genitori",
  "Percorsi diurni, notturni o integrati",
  "Massaggio infantile e tocco relazionale",
  "Movimento e psicomotricità precoce",
];

const EVOLUTIVE_INCLUDES = [
  "Osservazione educativa dello sviluppo",
  "Attività personalizzate",
  "Laboratori sensoriali",
  "Psicomotricità",
  "Gioco simbolico",
  "Letture condivise",
  "Attività ispirate al metodo Montessori",
  "Sviluppo delle autonomie",
  "Supporto alle routine quotidiane",
  "Consulenza sul sonno",
  "Supporto allo svezzamento",
  "Abbandono del pannolino e del ciuccio",
  "Preparazione all'inserimento al nido",
  "Confronto e sostegno continuo ai genitori",
];

const ADVANTAGES = [
  "Un'unica figura educativa di riferimento",
  "Percorso completamente personalizzato",
  "Maggiore continuità nella relazione con il bambino",
  "Oltre 23 anni di esperienza nella prima infanzia",
  "Competenze specifiche nella fascia 0–36 mesi",
  "Integrazione con consulenze su sonno, svezzamento, pannolino, ciuccio",
  "Osservazione costante dei progressi",
  "Dialogo continuo con i genitori",
  "Maggiore flessibilità organizzativa",
  "Accompagnamento anche in vacanze e trasferte",
  "Approccio T.A.T.A.®: ascolto, rispetto, gradualità",
];

export default function CuraDomicilio() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bearY = useTransform(scrollYProgress, [0, 1], ["0%", "70%"]);
  const bearRotate = useTransform(scrollYProgress, [0, 1], [0, 25]);

  return (
    <div data-testid="cura-domicilio-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[85vh] pt-32 md:pt-40 pb-16 px-6 md:px-10 overflow-hidden">
        <div className="max-w-[1600px] mx-auto relative">
          <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— Cura Educativa a Domicilio</p>
          <h1 className="font-display text-[13vw] md:text-[10vw] lg:text-[13rem] leading-[0.82] text-[#3D2140]">
            <RevealText text={["Due percorsi.", <span key="a" className="text-[#FF2768]">Un unico</span>, "approccio."]} stagger={0.12} />
          </h1>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-12 gap-8">
            <p className="md:col-span-6 md:col-start-4 font-editorial text-2xl md:text-3xl text-[#3D2140] leading-snug italic">
              Ogni fase della crescita richiede competenze, attenzioni e strumenti differenti. Per questo la Cura Educativa a Domicilio si articola in due percorsi specialistici, pensati per ogni età.
            </p>
          </div>
        </div>

        <motion.img
          src={BRAND.logo}
          alt=""
          aria-hidden
          style={{ y: bearY, rotate: bearRotate }}
          className="absolute right-4 md:right-16 top-24 md:top-40 h-32 md:h-52 w-32 md:w-52 rounded-full opacity-80"
        />
      </section>

      {/* TWO PATHS INTRO */}
      <section className="px-6 md:px-10 py-16 bg-[#81C3C9]/25" data-testid="two-paths-section">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
          <PathCard
            range="0–6 mesi"
            title="Assistenza Neonatale Specialistica"
            desc="Un percorso dedicato ai primi mesi di vita. Accompagno la famiglia nella costruzione delle prime routine, nel benessere del neonato e nel sostegno alla genitorialità."
            icon={Baby}
            anchor="#neonatale"
            color="#FF2768"
          />
          <PathCard
            range="6–36 mesi"
            title="Percorso Educativo Evolutivo"
            desc="Un accompagnamento educativo continuativo che sostiene lo sviluppo attraverso l'osservazione, il gioco, la relazione e la costruzione graduale delle autonomie."
            icon={TrendingUp}
            anchor="#evolutivo"
            color="#81C3C9"
          />
        </div>
        <p className="mt-10 text-center max-w-3xl mx-auto text-[#3D2140]/75 leading-relaxed font-editorial text-xl italic">
          Entrambi i percorsi seguono il <strong>Metodo T.A.T.A.®</strong> — un approccio basato sul rispetto dei tempi del bambino, sull'ascolto e sulla collaborazione con la famiglia.
        </p>
      </section>

      {/* NEONATALE */}
      <section id="neonatale" className="px-6 md:px-10 py-24 md:py-32 max-w-[1600px] mx-auto scroll-mt-24" data-testid="neonatale-section">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 mb-16">
          <div className="md:col-span-5">
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— 0–6 mesi</p>
            <h2 className="font-display text-6xl md:text-8xl text-[#3D2140] leading-[0.88]">
              Assistenza<br/>Neonatale<br/><span className="text-[#FF2768]">Specialistica.</span>
            </h2>
          </div>
          <div className="md:col-span-7 space-y-5 text-lg text-[#3D2140]/85 leading-relaxed">
            <p>I primi mesi di vita rappresentano un periodo unico e delicato, in cui ogni giornata porta con sé nuove scoperte, cambiamenti e domande.</p>
            <p>L'Assistenza Neonatale Specialistica offre alle famiglie un <strong>supporto concreto, competente e rispettoso</strong>, aiutandole ad affrontare con serenità questa fase così importante.</p>
            <p>Attraverso una presenza educativa qualificata accompagno il neonato e i suoi genitori nella costruzione delle prime routine quotidiane. Ogni intervento è progettato su misura.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-7">
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#81C3C9] mb-6">— Il percorso comprende</p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
              {NEONATE_INCLUDES.map((item, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.4, delay: i * 0.03 }}
                  className="flex items-start gap-3 border-b border-[#3D2140]/12 py-3"
                >
                  <Check size={16} className="text-[#FF2768] mt-1 shrink-0" />
                  <span className="text-[#3D2140] text-base">{item}</span>
                </motion.li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-4 md:col-start-9 self-start">
            <div className="bg-[#FF2768] text-white p-8 md:p-10 rounded-[32px] sticky top-28">
              <p className="text-[10px] uppercase tracking-[0.35em] text-white/80 mb-4">— Il mio obiettivo</p>
              <p className="font-editorial text-2xl md:text-3xl leading-snug">
                Aiutare la famiglia a vivere i primi mesi con maggiore serenità, in un ambiente sicuro e rispettoso.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* EVOLUTIVO */}
      <section id="evolutivo" className="px-6 md:px-10 py-24 md:py-32 bg-[#FAF8F5] scroll-mt-24" data-testid="evolutivo-section">
        <div className="max-w-[1600px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 mb-16">
            <div className="md:col-span-5">
              <p className="text-[10px] uppercase tracking-[0.4em] text-[#81C3C9] mb-4">— 6–36 mesi</p>
              <h2 className="font-display text-6xl md:text-8xl text-[#3D2140] leading-[0.88]">
                Percorso<br/>Educativo<br/><span className="text-[#81C3C9]">Evolutivo.</span>
              </h2>
            </div>
            <div className="md:col-span-7 space-y-5 text-lg text-[#3D2140]/85 leading-relaxed">
              <p>Dopo i primi mesi il bambino inizia una nuova fase della sua crescita. Esplora, comunica, si muove con sempre maggiore autonomia, costruisce le prime relazioni e sviluppa la propria identità.</p>
              <p>Il mio lavoro <strong>evolve insieme al bambino</strong>, trasformandosi in un accompagnamento educativo continuativo, costruito attraverso l'osservazione, il gioco e una relazione di fiducia con tutta la famiglia.</p>
              <p>Non attività standard, ma percorsi personalizzati che rispettano i tempi evolutivi di ogni bambino e valorizzano le sue competenze.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-7">
              <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-6">— Il percorso comprende</p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                {EVOLUTIVE_INCLUDES.map((item, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.4, delay: i * 0.03 }}
                    className="flex items-start gap-3 border-b border-[#3D2140]/12 py-3"
                  >
                    <Check size={16} className="text-[#81C3C9] mt-1 shrink-0" />
                    <span className="text-[#3D2140] text-base">{item}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
            <div className="md:col-span-4 md:col-start-9 self-start">
              <div className="bg-[#81C3C9] text-[#3D2140] p-8 md:p-10 rounded-[32px] sticky top-28">
                <p className="text-[10px] uppercase tracking-[0.35em] text-[#3D2140]/70 mb-4">— Il mio obiettivo</p>
                <p className="font-editorial text-2xl md:text-3xl leading-snug">
                  Accompagnare ogni bambino verso autonomia, benessere e sviluppo armonico, insieme alla sua famiglia.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GENTLE CARE */}
      <section className="px-6 md:px-10 py-24 md:py-32 bg-[#3D2140] text-white relative overflow-hidden" data-testid="metodo-tata-section">
        <img src={BRAND.logo} alt="" aria-hidden className="absolute -right-24 -top-24 h-96 w-96 opacity-15" />
        <div className="max-w-[1600px] mx-auto relative">
          <p className="text-[10px] uppercase tracking-[0.4em] text-[#FFE45C] mb-4">— Il Metodo</p>
          <h2 className="font-display text-6xl md:text-8xl lg:text-9xl leading-[0.85]">
            Metodo <span className="text-[#FF2768]">T.A.T.A.®</span>
          </h2>
          <p className="mt-8 font-editorial text-2xl md:text-3xl text-white/85 max-w-2xl italic">
            "Ogni bambino è unico. Per questo non applico programmi uguali per tutti."
          </p>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
            {["Osservo", "Ascolto", "Progetto", "Accompagno"].map((verb, i) => (
              <motion.div
                key={verb}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="border-t-2 border-[#FF2768] pt-5"
              >
                <p className="font-editorial text-[#FFE45C] text-sm mb-2">0{i + 1}</p>
                <p className="font-display text-4xl md:text-5xl text-white">{verb}<span className="text-[#FF2768]">.</span></p>
              </motion.div>
            ))}
          </div>
          <p className="mt-14 max-w-2xl text-white/80 leading-relaxed text-lg">
            Per me prendersi cura significa <strong className="text-white">esserci davvero</strong>: con competenza, sensibilità, continuità e responsabilità. Non entro nelle case per prendermi cura di un bambino: entro nelle famiglie per costruire, insieme ai genitori, un percorso di crescita rispettoso e personalizzato.
          </p>
        </div>
      </section>

      {/* TARIFFA */}
      <section className="px-6 md:px-10 py-24 md:py-32 max-w-[1600px] mx-auto" data-testid="pricing-section">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-5">
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— Formula oraria</p>
            <h2 className="font-display text-6xl md:text-8xl text-[#3D2140] leading-[0.88]">
              Più di un<br/><span className="text-[#FF2768]">percorso di<br/>assistenza.</span>
            </h2>
            <p className="mt-6 text-[#3D2140]/75 leading-relaxed max-w-md">
              Ogni incontro comprende molto più della semplice presenza: <strong>osservazione educativa</strong>, progettazione delle attività, confronto con la famiglia e adattamento costante del percorso.
            </p>
          </div>

          <div className="md:col-span-6 md:col-start-7">
            <div className="border-2 border-[#FF2768] rounded-[32px] p-8 md:p-12 relative overflow-hidden">
              <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-[#FF2768]/10 blur-2xl" />
              <p className="text-[10px] uppercase tracking-[0.35em] text-[#FF2768] mb-4">— Tariffa</p>
              <p className="font-display text-6xl md:text-7xl leading-[0.95] text-[#3D2140]">
                Preventivo<br/><span className="text-[#FF2768]">personalizzato</span>
              </p>
              <p className="mt-4 text-sm text-[#3D2140]/70 leading-relaxed">
                Ogni percorso viene concordato con la famiglia in base alle esigenze specifiche: età del bambino, durata, frequenza degli incontri, numero di bambini. Dopo un primo contatto riceverai un preventivo su misura, senza impegno.
              </p>
              <p className="mt-6 text-[10px] uppercase tracking-[0.35em] text-[#81C3C9] mb-3">— Il percorso comprende</p>
              <ul className="space-y-2 text-sm text-[#3D2140]/85">
                {[
                  "Presenza educativa professionale al domicilio",
                  "Osservazione dello sviluppo del bambino",
                  "Attività educative personalizzate",
                  "Sostegno alle autonomie",
                  "Accompagnamento nelle routine",
                  "Confronto educativo con la famiglia",
                  "Continuità del percorso nel tempo",
                ].map((i) => (
                  <li key={i} className="flex items-start gap-2"><Check size={14} className="text-[#FF2768] mt-1 shrink-0" /> {i}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ADVANTAGES */}
      <section className="px-6 md:px-10 py-24 md:py-32 bg-[#81C3C9]/25" data-testid="advantages-section">
        <div className="max-w-[1600px] mx-auto">
          <div className="mb-16 max-w-3xl">
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— Perché scegliere una libera professionista</p>
            <h2 className="font-display text-6xl md:text-8xl text-[#3D2140] leading-[0.9]">
              Un percorso<br/>su misura,<br/><span className="text-[#FF2768]">senza schemi.</span>
            </h2>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-1">
            {ADVANTAGES.map((a, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.4, delay: (i % 6) * 0.04 }}
                className="flex items-baseline gap-4 border-b border-[#3D2140]/15 py-4 group"
              >
                <span className="font-editorial text-[#FF2768] text-sm shrink-0 w-6">0{i + 1}</span>
                <span className="text-lg md:text-xl text-[#3D2140] group-hover:text-[#FF2768] transition-colors">{a}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </section>

      {/* PRENOTAZIONE */}
      <section className="px-6 md:px-10 py-24 md:py-32 max-w-[1600px] mx-auto" data-testid="how-to-book-section">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— Come prenotare</p>
            <h2 className="font-display text-6xl md:text-8xl text-[#3D2140] leading-[0.9]">
              Semplice.<br/><span className="text-[#FF2768]">Online.</span>
            </h2>
            <p className="mt-6 text-[#3D2140]/75 leading-relaxed">
              La prenotazione avviene in modo semplice e completamente online tramite il mio calendario <strong>Calendly</strong>. Il link viene fornito al momento del primo contatto: potrai poi scegliere in autonomia data e orario tra gli slot disponibili.
            </p>
          </div>

          <div className="md:col-span-7 space-y-6">
            <InfoRow icon={Clock} title="Durata degli appuntamenti" body="Slot prenotabili fino a un massimo di 3 ore consecutive — tempo necessario per garantire un intervento educativo efficace e rispettoso." />
            <InfoRow icon={Calendar} title="Più slot nella stessa giornata" body="Possibilità di prenotare più slot consecutivi o distribuiti, costruendo un intervento personalizzato sui bisogni del bambino e della famiglia." />
            <InfoRow icon={Home} title="Disponibilità" body="Disponibile prevalentemente nei giorni feriali. Richieste particolari vengono concordate durante il primo colloquio." />
            <InfoRow icon={Check} title="Conferma prenotazione" body="Ogni richiesta è soggetta a conferma di disponibilità. A conferma riceverai le informazioni organizzative, le modalità di pagamento e la documentazione necessaria." />
          </div>
        </div>

        <div className="mt-16 flex flex-wrap gap-3">
          <Link to="/prenota" data-testid="cura-cta-booking" className="btn-tv-primary">
            Richiedi la Cura Educativa a Domicilio <ArrowRight size={16} />
          </Link>
          <a href={BRAND.whatsappUrl} target="_blank" rel="noopener noreferrer" data-testid="cura-cta-whatsapp" className="btn-tv-outline">
            Info su WhatsApp
          </a>
        </div>
      </section>
    </div>
  );
}

function PathCard({ range, title, desc, icon: Icon, anchor, color }) {
  return (
    <motion.a
      href={anchor}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.6 }}
      className="group block bg-white rounded-[32px] p-8 md:p-10 border border-transparent hover:border-[color:var(--accent)] transition-colors"
      style={{ "--accent": color }}
    >
      <div className="flex items-center gap-4 mb-6">
        <span className="h-14 w-14 rounded-2xl flex items-center justify-center" style={{ background: color, color: "#fff" }}>
          <Icon size={22} />
        </span>
        <span className="text-xs uppercase tracking-[0.35em] font-semibold" style={{ color }}>{range}</span>
      </div>
      <h3 className="font-display text-4xl md:text-5xl text-[#3D2140] leading-[0.95] mb-4 group-hover:text-[color:var(--accent)] transition-colors" style={{ "--accent": color }}>
        {title}
      </h3>
      <p className="text-[#3D2140]/75 leading-relaxed mb-6">{desc}</p>
      <span className="inline-flex items-center gap-2 text-[#3D2140] font-semibold text-sm uppercase tracking-wider link-underline">
        Scopri il percorso <ArrowRight size={14} />
      </span>
    </motion.a>
  );
}

function InfoRow({ icon: Icon, title, body }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.5 }}
      className="flex items-start gap-5 border-b border-[#3D2140]/15 pb-6"
    >
      <span className="h-12 w-12 rounded-2xl bg-[#FF2768]/10 text-[#FF2768] flex items-center justify-center shrink-0">
        <Icon size={20} />
      </span>
      <div>
        <p className="font-display text-2xl md:text-3xl text-[#3D2140] mb-1">{title}</p>
        <p className="text-[#3D2140]/75 leading-relaxed">{body}</p>
      </div>
    </motion.div>
  );
}
